import dayjs from 'dayjs';
import { produce } from 'immer';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, To, NavigateOptions } from 'react-router-dom';
import { useAppLocation } from './reactRouter';
import {
  useScrollToTheTop,
  PersistentFiltersType,
  VisitListApplyFilter,
  DatesFilter,
  getPersistentFiltersFromLocalStorage,
  STORAGE_KEY,
} from './entity';

import { SubscriptionPlans } from '@features/subscription/entity';
import { MapReturnVisitFilter, VisitViewKey } from '@features/visits/entity';
import { useAuthContext } from '@shared/auth/AuthContext';
import { useSubscriptionQuery } from '@shared/queries';

export const useAppNavigate = () => {
  const navigate = useNavigate();
  const location = useAppLocation();
  const scroll = useScrollToTheTop();

  useEffect(() => {
    if (location.hash) {
      return;
    }

    scroll();
  }, [location.pathname, location.hash]);

  function appNavigate(to: To, options?: NavigateOptions): void;
  function appNavigate(delta: number): void;
  function appNavigate(toOrDelta: To | number, options?: NavigateOptions): void {
    if (typeof toOrDelta === 'number') {
      navigate(toOrDelta);
    } else {
      navigate(toOrDelta, options);
    }
  }

  return appNavigate;
};

export const usePersistentFilters = () => {
  const { isDemo } = useAuthContext();
  const [filters, setFilters] = useState<PersistentFiltersType>(() =>
    getPersistentFiltersFromLocalStorage(isDemo),
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const updateFilters = (updater: (draft: typeof filters) => void) => {
    setFilters(prev => produce(prev, updater));
  };

  return [filters, updateFilters] as const;
};

export const useVisitListFilters = (
  type: VisitListApplyFilter,
): MapReturnVisitFilter<VisitListApplyFilter> => {
  const [filters, updateFilter] = usePersistentFilters();

  const scopedUpdater = useCallback(
    (updater: (draft: { dates: DatesFilter; view: VisitViewKey }) => void) => {
      updateFilter(draft => {
        if (type === 'dashBoardVisitOverView') {
          updater(draft.visits.dashBoardVisitOverView);
        } else if (type === 'allVisitsPage') {
          updater(draft.visits.allVisitsPage);
        }
      });
    },
    [type, updateFilter],
  );

  if (type === 'dashBoardVisitOverView') {
    return [filters.visits.dashBoardVisitOverView, scopedUpdater];
  }

  if (type === 'allVisitsPage') {
    return [filters.visits.allVisitsPage, scopedUpdater];
  }

  return [
    {
      dates: {
        from: dayjs().subtract(1, 'day').toISOString(),
        to: dayjs().add(1, 'day').toISOString(),
      },
      view: 'byAll',
    },
  ];
};

export function useDebounce<T>(value: T, delay: number) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debounced;
}

export const useSubscriptionPlan = (): undefined | SubscriptionPlans => {
  const { data: subscription } = useSubscriptionQuery();

  return subscription ? subscription.plan : undefined;
};
